import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Scale, ArrowLeft } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";

const TIER_DEFS = [
  { key: "free", name: "Free", price: "Rp0", featsKey: "pricing.freeF" },
  { key: "pro", name: "Pro", price: "Rp149.000", highlight: true, featsKey: "pricing.proF" },
  { key: "bisnis", name: "Bisnis", price: "Rp399.000", featsKey: "pricing.bisnisF" },
];

export default function Pricing() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/billing/me").then((r) => setMe(r.data)).catch(() => setMe({ plan: null })); }, []);

  const upgrade = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.assign(data.checkout_url);
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };

  const current = me?.plan;
  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100">
      <header className="flex items-center gap-3 px-6 py-5 border-b border-zinc-800/70">
        <button onClick={() => nav(-1)} className="text-zinc-500 hover:text-zinc-200"><ArrowLeft className="h-5 w-5" /></button>
        <Scale className="h-5 w-5 text-[#d4af37]" strokeWidth={1.5} />
        <span className="font-mono-ui text-sm tracking-[0.25em] uppercase">Lexiqa</span>
        <div className="ml-auto"><LanguageToggle /></div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-16">
        <h1 className="font-serif-display text-4xl sm:text-5xl font-light">{t("pricing.h1")}</h1>
        <p className="mt-3 text-zinc-500">{t("pricing.sub")}</p>
        {current === "admin" && <p className="mt-4 inline-block rounded-full border border-[#d4af37]/30 bg-[#d4af37]/10 px-3 py-1 font-mono-ui text-xs text-[#d4af37]">{t("pricing.adminBadge")}</p>}
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {TIER_DEFS.map((tier) => (
            <div key={tier.key} data-testid={`tier-${tier.key}`}
              className={`flex flex-col rounded-2xl border p-7 ${tier.highlight ? "border-[#d4af37]/40 bg-[#121216]" : "border-zinc-800 bg-[#101014]"}`}>
              <h3 className="font-serif-display text-2xl font-light">{tier.name}</h3>
              <p className="mt-2"><span className="text-3xl font-light">{tier.price}</span><span className="text-zinc-500 text-sm">{tier.key === "free" ? "" : t("pricing.perMo")}</span></p>
              <ul className="mt-5 space-y-2 flex-1">
                {t(tier.featsKey).split("|").map((f) => <li key={f} className="flex items-start gap-2 text-sm text-zinc-300"><Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />{f}</li>)}
              </ul>
              {tier.key === "free" || current === "admin" ? (
                <Button disabled variant="outline" className="mt-6 h-11 border-zinc-700 text-zinc-500 rounded-full">
                  {current === tier.key ? t("pricing.yourPlan") : (current === "admin" ? "—" : t("pricing.free"))}
                </Button>
              ) : current === tier.key ? (
                <Button disabled className="mt-6 h-11 rounded-full bg-emerald-500/20 text-emerald-300">{t("pricing.activePlan")}</Button>
              ) : (
                <Button data-testid={`upgrade-${tier.key}`} onClick={() => upgrade(tier.key)} disabled={!!busy}
                  className={`mt-6 h-11 rounded-full ${tier.highlight ? "bg-[#d4af37] text-zinc-950 hover:bg-[#e6c04d]" : "bg-zinc-50 text-zinc-950 hover:bg-white"}`}>
                  {busy === tier.key ? <Loader2 className="h-4 w-4 animate-spin" /> : t("pricing.upgradeTo", { name: tier.name })}
                </Button>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
